import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Cpu, Users, Target } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";

const SKILLS = [
  "Python", "Django", "FastAPI", "REST APIs", "Generative AI", "RAG", "GraphRAG",
  "Neo4j", "LangChain", "n8n", "AWS", "Azure", "Airflow", "Kafka", "Redis",
  "MySQL", "PostgreSQL", "MongoDB", "CosmosDB", "Docker", "Kubernetes",
];

const VALUES = [
  { icon: Target, t: "Quality first", d: "We hold a high bar on architecture, code and outcomes — no shortcuts." },
  { icon: Users, t: "Long-term partnership", d: "We invest in relationships and stay with clients well beyond launch." },
  { icon: Cpu, t: "Cutting-edge AI", d: "We work at the frontier: GraphRAG, multi-agent systems and automation." },
];

export default function About() {
  usePageMeta({
    title: "About",
    description:
      "Growing IT Solutions is an Ahmedabad-based AI engineering company with 10+ years of experience, serving clients in India and worldwide. Quality-first, long-term AI partners.",
    path: "/about",
  });
  return (
    <div data-testid="about-page">
      <section className="relative grid-bg border-b border-white/10">
        <div className="absolute inset-0 radial-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-36 pb-20 sm:pt-44">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">About Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-4xl leading-[1.07]">
              We help businesses grow with AI they can trust.
            </h1>
            <p className="mt-6 text-slate-400 max-w-2xl text-lg leading-relaxed">
              Growing IT Solutions is an AI engineering company based in Ahmedabad, India, serving clients
              both in India and worldwide. With 10+ years of deep software and AI experience, we turn
              ambitious ideas into reliable, production-grade systems.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold leading-tight">Engineering excellence, with a human touch.</h2>
          <div className="mt-6 space-y-4 text-slate-400 leading-relaxed">
            <p>
              Over the last decade we've architected and shipped systems across generative AI, data
              engineering, automation and large-scale backends — from GraphRAG knowledge platforms to
              data pipelines handling 500,000+ records.
            </p>
            <p>
              Our approach is simple: senior engineers, clear communication, and a genuine commitment to
              every client's long-term success. We don't just deliver projects — we become your AI partner.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-px bg-white/5 border border-white/10">
            {[{ v: "10+", l: "Years" }, { v: "20+", l: "Projects" }, { v: "Global", l: "Clients" }].map((s, i) => (
              <div key={i} className="bg-[#0F111A] px-5 py-6 text-center">
                <p className="font-heading text-2xl font-semibold text-white">{s.v}</p>
                <p className="text-xs text-slate-400 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1621831337128-35676ca30868?auto=format&fit=crop&w=1200&q=80" alt="Global reach" className="w-full h-[420px] object-cover border border-white/10" />
            <div className="absolute -bottom-6 -left-6 glass border border-white/10 p-6 max-w-xs hidden sm:block">
              <p className="font-heading text-lg font-semibold text-white">Led by Yash</p>
              <p className="text-sm text-slate-400 mt-1">Python Architect &amp; AI Engineering Lead · 10+ years</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Values */}
      <section className="border-t border-white/10 bg-[#070810]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <Reveal><h2 className="font-heading text-3xl sm:text-4xl font-semibold text-center max-w-2xl mx-auto">What we stand for</h2></Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="border border-white/10 bg-[#0F111A] p-8 h-full">
                  <div className="w-12 h-12 grid place-items-center border border-primary/30 bg-primary/10 text-primary mb-6">
                    <v.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">{v.t}</h3>
                  <p className="text-sm text-slate-400 mt-3 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <Reveal>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">Our Stack</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold max-w-2xl">Technologies we've mastered.</h2>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 flex flex-wrap gap-3">
            {SKILLS.map((s) => (
              <span key={s} className="inline-flex items-center gap-2 text-sm font-mono text-slate-300 border border-white/10 bg-[#0F111A] px-4 py-2 hover:border-primary/40 transition-colors">
                <Check className="w-3.5 h-3.5 text-primary" /> {s}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold">Let's build your AI advantage.</h2>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-8 bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 glow-blue transition-all">
            Get in touch <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
